// In: apps/web/src/data-hub/modals/pnl-target.ts
import { doc, getDoc } from 'firebase/firestore'
import { db } from '@/core/firebase'
import { currentUser } from '@/core/state'
import { calculateAllPnlMetrics } from '@/analysis/utils/pnl'
import { AlsoStoreFn, createMaybeAlsoStoreFn } from '@/utils/also-store'
import paths from '@/db/paths'
import { branchContextService } from '@/services/branchContextService'

export async function showPnlTargetModal(targetData: any, reportId: string, config?: { alsoStore?: AlsoStoreFn, branchId?: string }) {
  const modal = document.getElementById('pnl-target-modal')
  const titleEl = document.getElementById('pnl-target-modal-title')
  const bodyEl = document.getElementById('pnl-target-modal-body')
  if (!modal || !titleEl || !bodyEl) return
  if (!currentUser) return

  bodyEl.innerHTML = '<p id="pnl-target-loading-msg" class="text-center text-gray-500">Loading actual P&L report for comparison...</p>'
  modal.classList.remove('hidden')

  const period = targetData.period
  // Simple fallback for title if period is missing (prevents crash)
  const periodDisplay = period ? period : 'Selected Period'
  
  titleEl.textContent = `P&L Target vs Actual for ${periodDisplay}`

  try {
    // Get branch ID from config or context
    const branchId = config?.branchId || branchContextService.requireBranchId()
    const pnlDocRef = doc(db, paths.branches(branchId).pnlReports.collectionPath, reportId)
    const pnlDocSnap = await getDoc(pnlDocRef)

    let actualValues = {
      'Pendapatan (Revenue)': 0, 'Harga Pokok Produksi': 0, 'Beban Operasional (OPEX)': 0,
      'Beban Non Operasional': 0, 'Depresiasi/ Amortisasi': 0, 'Bunga': 0, 'Pajak (PB1)': 0,
      'Laba Kotor (Gross Profit)': 0, 'Pendapatan Bersih Operasional (Net Operating Income)': 0,
      'Pendapatan Bersih Sebelum Deprisiasi/Amortisasi, Bunga & Pajak (EBITDA)': 0,
      'Pendapatan Bersih (Net Income)': 0,
    }

    if (pnlDocSnap.exists()) {
      const pnlData = pnlDocSnap.data().pnlData || {}
      const pnlMetrics = calculateAllPnlMetrics(pnlData)
      actualValues = { ...actualValues, ...pnlMetrics }
    }

    const targets = targetData.targets || {}
    const formatCurrency = (value: number) => `Rp${Math.round(value).toLocaleString('id-ID')}`

    let tableHtml = `
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Metric</th>
              <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Target</th>
              <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actual</th>
              <th class="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Change (%)</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden">Achievement</th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">`

    const metricOrder = [
      'Pendapatan (Revenue)', 'Harga Pokok Produksi', 'Laba Kotor (Gross Profit)',
      'Beban Operasional (OPEX)', 'Pendapatan Bersih Operasional (Net Operating Income)',
      'Beban Non Operasional', 'Pendapatan Bersih Sebelum Deprisiasi/Amortisasi, Bunga & Pajak (EBITDA)',
      'Depresiasi/ Amortisasi', 'Bunga', 'Pajak (PB1)', 'Pendapatan Bersih (Net Income)',
    ]
    // Also include specific expense lines if they exist in targets
    if (targets['Wages']) metricOrder.splice(4, 0, 'Wages') // Insert after OPEX
    if (targets['Rent']) metricOrder.splice(5, 0, 'Rent')

    const alsoStore = createMaybeAlsoStoreFn(config?.alsoStore)

    metricOrder.forEach((metric) => {
      // --- [THIS IS THE FIX] ---
      // Simplified logic: Just take the value directly.
      // The caller (tables.ts) is responsible for calculating the absolute numbers.
      const targetValue = targets[metric] || 0
      // --- [END FIX] ---

      // For sub-items like Wages/Rent, we need to find the actual value from the raw pnlData
      let actualValue = (actualValues as any)[metric] || 0
      
      // Special handling to find actuals for Wages/Rent if not in top-level metrics
      if (actualValue === 0 && pnlDocSnap.exists()) {
        const pnlData = pnlDocSnap.data().pnlData || {}
        if (metric === 'Wages') actualValue = pnlData['Beban Operasional (OPEX)']?.['Wages'] || 0
        if (metric === 'Rent') actualValue = pnlData['Beban Operasional (OPEX)']?.['Rent'] || 0
      }

      const achievement = targetValue > 0 ? (actualValue / targetValue) * 100 : 0
      const isCost = metric.toLowerCase().includes('beban') || metric.toLowerCase().includes('harga pokok') || metric === 'Wages' || metric === 'Rent'

      const change = actualValue - targetValue
      let percentageChangeText = 'N/A'
      
      // If we have both values, calculate variance
      if (targetValue !== 0 && actualValue !== 0) {
        const percentage = (change / targetValue) * 100
        percentageChangeText = `${percentage >= 0 ? '+' : ''}${percentage.toFixed(1)}%`
      } 
      // If we only have actual but no target
      else if (targetValue === 0 && actualValue !== 0) {
        percentageChangeText = 'New'
      }
      // If we have target but no actual (0)
      else if (targetValue !== 0 && actualValue === 0) {
        percentageChangeText = '-100%'
      }

      let changeColor = 'text-gray-500'
      if (percentageChangeText !== 'N/A' && percentageChangeText !== 'New') {
        // For costs: Positive change (Over budget) is RED
        // For revenue: Positive change (Over target) is GREEN
        if (isCost) {
            changeColor = change > 0 ? 'text-red-600' : 'text-green-600'
        } else {
            changeColor = change > 0 ? 'text-green-600' : 'text-red-600'
        }
      }

      alsoStore(formatCurrency(targetValue), (v) => ({ monthlyPnL_vs_target: { targets: { [metric]: v } } }))
      alsoStore(formatCurrency(actualValue), (v) => ({ monthlyPnL_vs_target: { actuals: { [metric]: v } } }))
      alsoStore(percentageChangeText, (v) => ({ monthlyPnL_vs_target: { changes: { [metric]: v } } }))

      tableHtml += `
        <tr>
          <td class="px-6 py-4 text-sm font-medium text-gray-900">${metric}</td>
          <td class="px-6 py-4 text-sm text-gray-500 text-right font-mono">${formatCurrency(targetValue)}</td>
          <td class="px-6 py-4 text-sm text-gray-500 text-right font-mono">${formatCurrency(actualValue)}</td>
          <td class="px-6 py-4 text-sm text-center font-semibold ${changeColor}">${percentageChangeText}</td>
          <td class="px-6 py-4 text-sm text-gray-500 hidden">
            <div class="flex items-center">
              <span class="font-semibold">${achievement.toFixed(1)}%</span>
            </div>
          </td>
        </tr>`
    })

    tableHtml += `</tbody></table></div>`
    bodyEl.innerHTML = tableHtml
  } catch (error: any) {
    console.error('Error processing P&L target view:', error)
    bodyEl.innerHTML = `<p class="text-center text-red-500">Error: Could not display P&L target comparison. ${error.message}</p>`
  }
}